import type { Resource } from "@medplum/fhirtypes";

/** Subject Patient is included in suspect bundles for references; omit from evidence UI. */
export function filterEvidenceResourcesExcludingPatient(resources: Resource[]): Resource[] {
  return resources.filter(function excludePatient(r) {
    return r.resourceType !== "Patient";
  });
}

function simpleStringHash(input: string): string {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (Math.imul(31, h) + input.charCodeAt(i)) | 0;
  }
  return String(h >>> 0);
}

/**
 * Stable React list key for FHIR evidence resources: prefer logical identity, else content hash.
 */
export function evidenceResourceListKey(resource: Resource, index: number): string {
  const id =
    resource.id != null && String(resource.id).length > 0 ? String(resource.id) : undefined;
  const versionId =
    resource.meta?.versionId != null && String(resource.meta.versionId).length > 0
      ? String(resource.meta.versionId)
      : undefined;
  if (id != null) {
    return `${resource.resourceType}/${id}/${versionId ?? ""}`;
  }
  const payload = JSON.stringify(resource);
  return `${resource.resourceType}/h${simpleStringHash(payload)}-i${index}`;
}

function evidenceResourceTimeMs(iso: string | undefined): number {
  if (!iso) {
    return 0;
  }
  const parsed = Date.parse(iso);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Sort evidence newest-first; deterministic when dates tie (type/id).
 */
export function compareEvidenceResourcesDesc(
  a: Resource,
  b: Resource,
  getDate: (r: Resource) => string | undefined
): number {
  const aTime = evidenceResourceTimeMs(getDate(a));
  const bTime = evidenceResourceTimeMs(getDate(b));
  if (bTime !== aTime) {
    return bTime - aTime;
  }
  const aKey = `${a.resourceType}/${a.id ?? ""}`;
  const bKey = `${b.resourceType}/${b.id ?? ""}`;
  return aKey.localeCompare(bKey);
}
